import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Loader2, X, Heart, CheckCircle, MapPin } from "lucide-react";
import axios from "axios";
import LocationPicker from "../components/LocationPicker";
import { API } from "../utils/api";

// Fleety Logo Component
const FleetyLogo = ({ className = "", size = "default" }) => {
  const sizeClasses = {
    small: "text-2xl",
    default: "text-4xl md:text-5xl",
    large: "text-5xl md:text-6xl"
  };
  
  return (
    <div className={`fleety-logo ${sizeClasses[size]} ${className}`} data-testid="fleety-logo">
      <span className="text-[#3FA9F5]">F</span>
      <span className="text-[#3FA9F5]">leety</span>
    </div>
  );
};

// Waitlist Popup Component
const WaitlistPopup = ({ pincode, onClose }) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async () => {
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);
    setError("");
    
    try {
      await axios.post(`${API}/waitlist`, {
        email: email,
        pincode: pincode
      });
      setIsSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4 animate-fadeIn"
      data-testid="waitlist-popup"
    >
      <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl text-center animate-slideUp relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="close-waitlist-popup"
        >
          <X size={24} />
        </button>
        
        {!isSubmitted ? (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-pink-100 flex items-center justify-center">
              <Heart className="w-10 h-10 text-pink-500" fill="#EC4899" />
            </div>
            
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">
              Thank you for your love
            </h2>
            <p className="text-gray-500 mb-6 text-sm">
              Please provide your details, we will notify you as soon as we go live
            </p>
            
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              className="w-full h-14 px-6 text-lg rounded-full border-2 border-gray-100 focus:border-[#3FA9F5] mb-4 fleety-input"
              data-testid="waitlist-email-input"
            />
            
            {error && (
              <p className="text-red-500 text-sm mb-4">{error}</p>
            )}
            
            <Button 
              onClick={handleSubmit}
              disabled={!email || isSubmitting}
              className={`w-full h-14 text-white text-lg font-semibold rounded-full transition-all ${
                email && isValidEmail(email) 
                  ? 'bg-[#22C55E] hover:bg-[#16A34A] shadow-lg' 
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              data-testid="notify-me-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                "Notify Me"
              )}
            </Button>
          </>
        ) : (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#22C55E] flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">
              You&apos;re on the list!
            </h2>
            <p className="text-gray-500 mb-6">
              We&apos;ll notify you at <span className="font-medium text-gray-700">{email}</span> when we launch in your area.
            </p>
            
            <Button 
              onClick={onClose}
              className="w-full h-14 bg-[#3FA9F5] hover:bg-[#2196F3] text-white text-lg font-semibold rounded-full shadow-fleety transition-all"
              data-testid="close-confirmed-btn"
            >
              Got it!
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const SignupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phone_number: "",
    full_name: "",
    email: "",
    label: "",
    address_line1: "",
    address_line2: "",
    address_line3: "",
    pincode: "",
    lat: null,
    lng: null
  });
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showWaitlistPopup, setShowWaitlistPopup] = useState(false);
  const [enteredPincode, setEnteredPincode] = useState("");
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocationAddress, setSelectedLocationAddress] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [successMessage, setSuccessMessage] = useState("");

  // Countdown timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleLocationSelect = (location) => {
    const pincodeMatch = location.address.match(/\b\d{6}\b/);
    const pincode = pincodeMatch ? pincodeMatch[0] : "";
    
    setSelectedLocationAddress(location.address);
    setFormData(prev => ({
      ...prev,
      lat: location.lat,
      lng: location.lng,
      pincode: pincode || ""
    }));
    setShowLocationPicker(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "phone_number") {
      const cleaned = value.replace(/\D/g, "").slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
      return;
    }
    
    if (name === "pincode") {
      const cleaned = value.replace(/\D/g, "").slice(0, 6);
      setFormData(prev => ({ ...prev, [name]: cleaned }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setOtp(value);
    setError("");
  };

  const validateForm = () => {
    if (!formData.phone_number || formData.phone_number.length !== 10) {
      setError("Please enter a valid 10-digit phone number");
      return false;
    }
    if (!formData.full_name.trim()) {
      setError("Please enter your full name");
      return false;
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!formData.label.trim()) {
      setError("Please enter a label (e.g., Home, Office, Work)");
      return false;
    }
    if (!formData.address_line1.trim()) {
      setError("Please enter Flat/House No/Building Name");
      return false;
    }
    if (!formData.address_line2.trim()) {
      setError("Please enter Street/Area/Landmark");
      return false;
    }
    if (formData.pincode && formData.pincode.length !== 6) {
      setError("Please enter a valid 6-digit pincode");
      return false;
    }
    if (!agreedToTerms) {
      setTermsError(true);
      setError("Please agree to the Terms & Conditions to continue");
      return false;
    }
    return true;
  };

  const handleGetOtp = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Call backend to send OTP
      const response = await axios.post(`${API}/signup-send-otp`, formData);
      
      if (response.data.success) {
        setOtpSent(true);
        setResendTimer(30);
        setSuccessMessage("OTP sent to your phone!");
      } else {
        setError(response.data.message || "Failed to send OTP");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Something went wrong. Please try again.";
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length < 4) {
      setError("Please enter the OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/signup-verify-otp`, {
        phone_number: formData.phone_number,
        otp: otp
      });

      if (response.data.success) {
        localStorage.setItem("fleety_user", JSON.stringify(response.data.user));
        navigate("/home");
      } else {
        setError(response.data.message || "Invalid OTP");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = () => {
    if (resendTimer === 0) {
      setOtp("");
      handleGetOtp({ preventDefault: () => {} });
    }
  };

  const handleBack = () => {
    if (otpSent) {
      setOtpSent(false);
      setOtp("");
      setError("");
      setSuccessMessage("");
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="signup-page">
      <div className="h-6 bg-[#3FA9F5]"></div>
      
      <header className="bg-[#3FA9F5] p-6 flex items-center gap-4">
        <button 
          onClick={handleBack}
          className="text-white hover:text-white/80 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <FleetyLogo size="small" className="text-white [&>span]:text-white" />
      </header>

      <main className="flex-1 flex flex-col px-6 pt-8 pb-10 overflow-y-auto">
        <div className="w-full max-w-md mx-auto">
          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">
            {otpSent ? "Verify OTP" : "Create Account"}
          </h1>
          <p className="text-gray-500 mb-6">
            {otpSent 
              ? `Enter the OTP sent to +91 ${formData.phone_number}`
              : "Fill in your details to get started"
            }
          </p>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-green-700">{successMessage}</span>
            </div>
          )}

          {!otpSent ? (
            <form onSubmit={handleGetOtp} className="space-y-4">
              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="h-14 px-4 flex items-center bg-gray-50 border-2 border-gray-100 rounded-xl text-gray-500">
                    +91
                  </div>
                  <Input
                    type="tel"
                    name="phone_number"
                    placeholder="Enter 10-digit number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    className="flex-1 h-14 px-6 text-lg rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-[#3FA9F5] focus:bg-white fleety-input"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="full_name"
                  placeholder="Enter your full name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full h-14 px-6 text-lg rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-[#3FA9F5] focus:bg-white fleety-input"
                  data-testid="fullname-input"
                />
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full h-14 px-6 text-lg rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-[#3FA9F5] focus:bg-white fleety-input"
                  data-testid="email-input"
                />
              </div>

              {/* Location Selection */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Delivery Location <span className="text-red-500">*</span>
                </label>
                
                <button
                  type="button"
                  onClick={() => setShowLocationPicker(true)}
                  className="w-full p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-[#3FA9F5] hover:bg-[#3FA9F5]/5 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center">
                      <MapPin size={20} className="text-[#3FA9F5]" />
                    </div>
                    <div className="flex-1">
                      {selectedLocationAddress ? (
                        <>
                          <p className="text-sm font-medium text-gray-900">Location Selected</p>
                          <p className="text-xs text-gray-500 line-clamp-2">{selectedLocationAddress}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-[#3FA9F5]">Select Location on Map</p>
                          <p className="text-xs text-gray-500">Search or pick your delivery location</p>
                        </>
                      )}
                    </div>
                  </div>
                </button>

                {/* Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Label <span className="text-gray-400 text-xs">(e.g., Home, Office)</span>
                  </label>
                  <div className="flex gap-2">
                    {["Home", "Office", "Other"].map((label) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, label }))}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          formData.label === label 
                            ? 'bg-[#3FA9F5] text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Address Details */}
                <Input
                  type="text"
                  name="address_line1"
                  placeholder="Flat / House No / Building Name"
                  value={formData.address_line1}
                  onChange={handleChange}
                  className="w-full h-12 px-6 text-base rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-[#3FA9F5] focus:bg-white fleety-input"
                  data-testid="address1-input"
                />
                <Input
                  type="text"
                  name="address_line2"
                  placeholder="Street / Area / Landmark"
                  value={formData.address_line2}
                  onChange={handleChange}
                  className="w-full h-12 px-6 text-base rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-[#3FA9F5] focus:bg-white fleety-input"
                  data-testid="address2-input"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm" data-testid="error-message">{error}</p>
              )}

              {/* Terms Checkbox */}
              <div className="mt-4">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => {
                        setAgreedToTerms(e.target.checked);
                        setTermsError(false);
                        if (e.target.checked) setError("");
                      }}
                      className="sr-only peer"
                      data-testid="terms-checkbox"
                    />
                    <div className={`w-5 h-5 border-2 rounded ${termsError ? 'border-red-500' : 'border-gray-300'} peer-checked:border-[#3FA9F5] peer-checked:bg-[#3FA9F5] transition-colors flex items-center justify-center`}>
                      {agreedToTerms && <CheckCircle size={14} className="text-white" />}
                    </div>
                  </div>
                  <span className={`text-sm ${termsError ? 'text-red-500' : 'text-gray-600'}`}>
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/terms");
                      }}
                      className="text-[#3FA9F5] font-medium hover:underline"
                    >
                      Terms & Conditions
                    </button>
                  </span>
                </label>
              </div>

              <Button 
                type="submit"
                disabled={isLoading}
                className="w-full h-14 mt-6 bg-[#3FA9F5] hover:bg-[#2196F3] text-white text-lg font-semibold rounded-full shadow-fleety transition-all disabled:opacity-50"
                data-testid="signup-submit-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  "Get OTP & Sign Up"
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              {/* OTP Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter OTP
                </label>
                <Input
                  type="tel"
                  placeholder="Enter 4-digit OTP"
                  value={otp}
                  onChange={handleOtpChange}
                  className="w-full h-14 px-6 text-xl text-center tracking-[0.5em] rounded-xl border-2 border-gray-100 bg-gray-50 focus:border-[#3FA9F5] focus:bg-white fleety-input"
                  data-testid="otp-input"
                  autoFocus
                  maxLength={4}
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center" data-testid="error-message">{error}</p>
              )}

              {/* Verify Button */}
              <Button 
                onClick={handleVerifyOtp}
                disabled={isLoading || otp.length < 4}
                className="w-full h-14 bg-[#3FA9F5] hover:bg-[#2196F3] text-white text-lg font-semibold rounded-full shadow-fleety transition-all disabled:opacity-50"
                data-testid="verify-otp-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  "Verify & Create Account"
                )}
              </Button>

              {/* Resend OTP */}
              <div className="text-center">
                {resendTimer > 0 ? (
                  <p className="text-gray-500 text-sm">
                    Resend OTP in <span className="font-semibold text-[#3FA9F5]">{resendTimer}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResendOtp}
                    className="text-[#3FA9F5] font-semibold text-sm hover:underline"
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </div>
          )}

          <p className="text-center mt-8 text-gray-500">
            Already have an account?{" "}
            <button 
              onClick={() => navigate("/login")}
              className="text-[#3FA9F5] font-semibold hover:underline"
              data-testid="login-link"
            >
              Log In
            </button>
          </p>
        </div>
      </main>

      {showWaitlistPopup && (
        <WaitlistPopup 
          pincode={enteredPincode}
          onClose={() => {
            setShowWaitlistPopup(false);
            navigate("/");
          }}
        />
      )}

      {showLocationPicker && (
        <LocationPicker
          onLocationSelect={handleLocationSelect}
          onClose={() => setShowLocationPicker(false)}
        />
      )}
    </div>
  );
};

export default SignupPage;
